import { useState } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle } from "lucide-react";

const ImportDialog = ({ onImportComplete }) => {
  const { getAuthHeaders, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1); // 1: upload, 2: mapping, 3: result
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mapping, setMapping] = useState({
    date_column: "",
    description_column: "",
    amount_column: "",
    category_column: "",
    payment_method_column: "",
    type_column: "",
    notes_column: "",
    invoiced_column: "",
    asset_column: "",
    default_type: "expense"
  });
  const [result, setResult] = useState(null);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      
      const response = await axios.post(`${API}/import/preview`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      setPreview(response.data);
      
      // Auto-detect columns based on common names
      const cols = response.data.columns.map(c => c.toLowerCase());
      const newMapping = { ...mapping };
      
      cols.forEach((col, idx) => {
        const originalCol = response.data.columns[idx];
        if (col.includes("fecha") || col.includes("date")) newMapping.date_column = originalCol;
        if (col.includes("descripci") || col.includes("concepto") || col.includes("description")) newMapping.description_column = originalCol;
        if (col.includes("costo") || col.includes("monto") || col.includes("amount") || col.includes("valor")) newMapping.amount_column = originalCol;
        if (col.includes("categor")) newMapping.category_column = originalCol;
        if (col.includes("método") || col.includes("metodo") || col.includes("pago") || col.includes("payment")) newMapping.payment_method_column = originalCol;
        if (col.includes("tipo") || col.includes("type")) newMapping.type_column = originalCol;
        if (col.includes("nota") || col.includes("note") || col.includes("observ")) newMapping.notes_column = originalCol;
        if (col.includes("factura") || col.includes("invoice")) newMapping.invoiced_column = originalCol;
        if (col.includes("activo") || col.includes("asset")) newMapping.asset_column = originalCol;
      });
      
      setMapping(newMapping);
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al leer el archivo");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!mapping.description_column || !mapping.amount_column) {
      toast.error("Debes seleccionar al menos Descripción y Monto");
      return;
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      // Build query params for mapping
      const params = new URLSearchParams();
      Object.entries(mapping).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await axios.post(
        `${API}/import/transactions?${params.toString()}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      setResult(response.data);
      setStep(3);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} transacciones importadas`);
        onImportComplete?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar");
    } finally {
      setLoading(false);
    }
  };

  const resetDialog = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setResult(null);
    setMapping({
      date_column: "",
      description_column: "",
      amount_column: "",
      category_column: "",
      payment_method_column: "",
      type_column: "",
      notes_column: "",
      invoiced_column: "",
      asset_column: "",
      default_type: "expense"
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetDialog(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="bg-white border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FEE2E2] w-full sm:w-auto" data-testid="import-button">
          <Upload className="w-4 h-4 mr-2" />
          Importar Excel
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white border-[#FECDD3] text-[#4A4A4A] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#F43F5E]" />
            Importar Transacciones desde Excel
          </DialogTitle>
        </DialogHeader>
        
        {/* Step 1: Upload */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-[#6B7280] text-sm">
              Sube un archivo Excel (.xlsx, .xls) o CSV con tus transacciones.
            </p>
            
            <div className="border-2 border-dashed border-[#FECDD3] rounded-lg p-8 text-center hover:border-[#F43F5E] transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileChange}
                className="hidden"
                id="excel-upload"
                data-testid="excel-file-input"
              />
              <label htmlFor="excel-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-[#6B7280] mb-4" />
                <p className="text-[#4A4A4A] font-medium">Haz clic para seleccionar archivo</p>
                <p className="text-[#6B7280] text-sm mt-1">o arrastra y suelta aquí</p>
              </label>
            </div>
            
            {loading && (
              <div className="flex items-center justify-center gap-2 text-[#6B7280]">
                <div className="spinner w-4 h-4"></div>
                Leyendo archivo...
              </div>
            )}
          </div>
        )}
        
        {/* Step 2: Mapping */}
        {step === 2 && preview && (
          <div className="space-y-4">
            <div className="bg-[#FEE2E2]/50 rounded-lg p-3 border border-[#FECDD3]">
              <p className="text-[#6B7280] text-sm">
                <span className="text-[#4A4A4A] font-medium">{file?.name}</span> - {preview.total_rows} filas encontradas
              </p>
            </div>
            
            <p className="text-[#6B7280] text-sm">
              Mapea las columnas de tu archivo a los campos del sistema:
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Required fields */}
              <div className="space-y-2">
                <Label className="text-[#6B7280]">Descripción *</Label>
                <Select value={mapping.description_column} onValueChange={(v) => setMapping({...mapping, description_column: v})}>
                  <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="map-description">
                    <SelectValue placeholder="Seleccionar columna" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                    {preview.columns.map((col) => (
                      <SelectItem key={col} value={col} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#6B7280]">Monto *</Label>
                <Select value={mapping.amount_column} onValueChange={(v) => setMapping({...mapping, amount_column: v})}>
                  <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="map-amount">
                    <SelectValue placeholder="Seleccionar columna" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                    {preview.columns.map((col) => (
                      <SelectItem key={col} value={col} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#6B7280]">Fecha</Label>
                <Select value={mapping.date_column || "_none"} onValueChange={(v) => setMapping({...mapping, date_column: v === "_none" ? "" : v})}>
                  <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="map-date">
                    <SelectValue placeholder="Seleccionar columna" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                    <SelectItem value="_none" className="text-[#6B7280] hover:bg-[#FEE2E2]">-- Sin mapear --</SelectItem>
                    {preview.columns.map((col) => (
                      <SelectItem key={col} value={col} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#6B7280]">Categoría</Label>
                <Select value={mapping.category_column || "_none"} onValueChange={(v) => setMapping({...mapping, category_column: v === "_none" ? "" : v})}>
                  <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="map-category">
                    <SelectValue placeholder="Seleccionar columna" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                    <SelectItem value="_none" className="text-[#6B7280] hover:bg-[#FEE2E2]">-- Sin mapear --</SelectItem>
                    {preview.columns.map((col) => (
                      <SelectItem key={col} value={col} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#6B7280]">Método de Pago</Label>
                <Select value={mapping.payment_method_column || "_none"} onValueChange={(v) => setMapping({...mapping, payment_method_column: v === "_none" ? "" : v})}>
                  <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="map-payment">
                    <SelectValue placeholder="Seleccionar columna" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                    <SelectItem value="_none" className="text-[#6B7280] hover:bg-[#FEE2E2]">-- Sin mapear --</SelectItem>
                    {preview.columns.map((col) => (
                      <SelectItem key={col} value={col} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#6B7280]">Notas</Label>
                <Select value={mapping.notes_column || "_none"} onValueChange={(v) => setMapping({...mapping, notes_column: v === "_none" ? "" : v})}>
                  <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="map-notes">
                    <SelectValue placeholder="Seleccionar columna" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                    <SelectItem value="_none" className="text-[#6B7280] hover:bg-[#FEE2E2]">-- Sin mapear --</SelectItem>
                    {preview.columns.map((col) => (
                      <SelectItem key={col} value={col} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#6B7280]">¿Facturado?</Label>
                <Select value={mapping.invoiced_column || "_none"} onValueChange={(v) => setMapping({...mapping, invoiced_column: v === "_none" ? "" : v})}>
                  <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="map-invoiced">
                    <SelectValue placeholder="Seleccionar columna" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                    <SelectItem value="_none" className="text-[#6B7280] hover:bg-[#FEE2E2]">-- Sin mapear --</SelectItem>
                    {preview.columns.map((col) => (
                      <SelectItem key={col} value={col} className="text-[#4A4A4A] hover:bg-[#FEE2E2]">{col}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#6B7280]">Tipo por defecto</Label>
                <Select value={mapping.default_type} onValueChange={(v) => setMapping({...mapping, default_type: v})}>
                  <SelectTrigger className="bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A]" data-testid="map-default-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#FEE2E2] border-[#FECDD3]">
                    <SelectItem value="expense" className="text-[#4A4A4A] hover:bg-[#FEE2E2]">Gasto</SelectItem>
                    <SelectItem value="income" className="text-[#4A4A4A] hover:bg-[#FEE2E2]">Ingreso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Preview table */}
            <div className="mt-4">
              <p className="text-[#6B7280] text-sm mb-2">Vista previa (primeras 5 filas):</p>
              <div className="overflow-x-auto bg-[#FEE2E2]/30 rounded-lg border border-[#FECDD3]">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#FEE2E2]">
                      {preview.columns.slice(0, 6).map((col) => (
                        <th key={col} className="p-2 text-left text-[#6B7280] font-medium">{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample_rows.slice(0, 5).map((row, idx) => (
                      <tr key={idx} className="border-t border-[#FECDD3]">
                        {preview.columns.slice(0, 6).map((col) => (
                          <td key={col} className="p-2 text-[#6B7280] truncate max-w-[150px]">{row[col]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 bg-[#FEE2E2] border-[#FECDD3] text-[#4A4A4A] hover:bg-[#FEE2E2]">
                Atrás
              </Button>
              <Button onClick={handleImport} disabled={loading} className="flex-1 bg-[#F43F5E] hover:bg-[#F43F5E]/80 text-white" data-testid="start-import-button">
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="spinner w-4 h-4"></div>
                    Importando...
                  </span>
                ) : (
                  `Importar ${preview.total_rows} filas`
                )}
              </Button>
            </div>
          </div>
        )}
        
        {/* Step 3: Result */}
        {step === 3 && result && (
          <div className="space-y-4">
            <div className="text-center py-6">
              {result.imported > 0 ? (
                <CheckCircle className="w-16 h-16 mx-auto text-[#10B981] mb-4" />
              ) : (
                <AlertCircle className="w-16 h-16 mx-auto text-[#F59E0B] mb-4" />
              )}
              <h3 className="text-xl font-bold text-[#4A4A4A]">Importación completada</h3>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#10B981]/10 border border-[#A8E6CF]/30 rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-[#10B981] font-mono">{result.imported}</p>
                <p className="text-[#6B7280] text-sm">Importadas</p>
              </div>
              <div className="bg-[#FEE2E2]/50 border border-[#FECDD3] rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-[#6B7280] font-mono">{result.skipped}</p>
                <p className="text-[#6B7280] text-sm">Omitidas</p>
              </div>
            </div>
            
            {result.errors.length > 0 && (
              <div className="bg-[#F43F5E]/10 border border-[#F43F5E]/30 rounded-lg p-4">
                <p className="text-[#F43F5E] font-medium mb-2">Errores encontrados:</p>
                <ul className="text-sm text-[#6B7280] space-y-1">
                  {result.errors.map((err, idx) => (
                    <li key={idx}>• {err}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <Button onClick={() => setOpen(false)} className="w-full bg-[#F43F5E] hover:bg-[#F43F5E]/80 text-white">
              Cerrar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
